/**
 * SC-425: the single rule for "this teleport would be a no-op", shared by the server resolver
 * (authoritative) and the client UI (disabled buttons) so the two can never disagree.
 *
 * Pure — no Phaser/Colyseus imports; positions are world pixels (the same unit the Follow
 * proximity check already uses), room ids are '' when standing on the open floor.
 */

/** Keep aligned with the client's DEFAULT_PROXIMITY_FALLBACK_PX (Follow/voice proximity). */
export const TELEPORT_PROXIMITY_PX = 100

export type TeleportRedundancyReason = 'same_room' | 'too_close'

export interface TeleportPoseLike {
  x: number
  y: number
  /** '' / undefined when outside any virtual room. */
  roomId?: string
}

export type TeleportRedundancyTarget =
  | { type: 'room'; roomId: string }
  | { type: 'player'; player: TeleportPoseLike }

export interface TeleportRedundancyResult {
  redundant: boolean
  reason: TeleportRedundancyReason | null
}

const NOT_REDUNDANT: TeleportRedundancyResult = { redundant: false, reason: null }

export function evaluateTeleportRedundancy(
  requester: TeleportPoseLike,
  target: TeleportRedundancyTarget,
  proximityPx: number = TELEPORT_PROXIMITY_PX,
): TeleportRedundancyResult {
  if (target.type === 'room') {
    return requester.roomId && requester.roomId === target.roomId
      ? { redundant: true, reason: 'same_room' }
      : NOT_REDUNDANT
  }

  const { player } = target
  if (requester.roomId && player.roomId && requester.roomId === player.roomId) {
    return { redundant: true, reason: 'same_room' }
  }
  const dx = player.x - requester.x
  const dy = player.y - requester.y
  if (dx * dx + dy * dy <= proximityPx * proximityPx) {
    return { redundant: true, reason: 'too_close' }
  }
  return NOT_REDUNDANT
}
